import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import useMutateWithToast from 'hooks/useMutateWithToast';
import Workflow, { WorkflowAction } from 'models/Workflow';
import { validationErrorMessage } from 'services/api/validationErrors';
import {
  createFacet,
  saveWorkflowActionProgress,
  updateFacet,
  updateWorkflow,
  workflowQueryKeys,
} from 'services/api/workflows';

import { DemographicConflict, DISMISSALS_OUTPUT, dismissalsFor } from './demographicConflicts';
import { impactedActionsFor, stillCurrentRestartIds } from './impactedActions';

// The two answers to a highlighted Patient Demographics field (OLIO-3252).
//
// **Use this value** QUEUES the packet's reading; **Apply** then saves every queued value at once.
// One save is the point: each correction re-runs the finished steps it feeds, and a name, then a
// birth date, then an SSN saved one by one would restart the same duplicate search three times over.
// Applied together they make one consent step (the union of what they ripple into) and one referral
// write, so each affected step re-runs once. The writes are the ones Edit Demographics makes, so a
// used value is recorded, narrated and outranks extraction exactly as a typed one would.
//
// **Dismiss** saves at once: it re-runs nothing. It records, on the step, that someone looked and kept
// the value in use — shared, stored on the referral, stamped by the API with who and when.
//
// `conflicts` are the card's current, undismissed conflicts. The queue holds their keys, not the
// conflicts themselves, so a queued field someone else resolved in the meantime simply drops out.
export default function useResolveDemographicConflict(
  workflow: Workflow,
  action: WorkflowAction,
  conflicts: DemographicConflict[]
) {
  const queryClient = useQueryClient();
  const [queuedKeys, setQueuedKeys] = useState<string[]>([]);
  // The steps the queued values would re-run, while the consent step is up.
  const [pendingRestart, setPendingRestart] = useState<WorkflowAction[] | null>(null);

  const queued = conflicts.filter((conflict) => queuedKeys.includes(conflict.key));

  // Apply the settled response so the acting user sees their own change at once — the same cache
  // write Edit Demographics makes.
  const cacheWorkflow = (saved: Workflow) => {
    queryClient.setQueryData(workflowQueryKeys.show(saved.id), saved);
    queryClient.invalidateQueries({ queryKey: workflowQueryKeys.listPrefix });
  };

  const handlers = { onSuccess: cacheWorkflow, errorToast: validationErrorMessage };
  const { mutateAsync: writeReferral, isPending: writingReferral } = useMutateWithToast({
    mutationFn: updateWorkflow,
    ...handlers,
  });
  const { mutateAsync: writeCoverage, isPending: writingCoverage } = useMutateWithToast({
    mutationFn: updateFacet,
    ...handlers,
  });
  const { mutateAsync: addCoverage, isPending: addingCoverage } = useMutateWithToast({
    mutationFn: createFacet,
    ...handlers,
  });
  const { mutateAsync: writeDismissals, isPending: dismissing } = useMutateWithToast({
    mutationFn: saveWorkflowActionProgress,
    ...handlers,
  });

  // The queue as writes: referral-level values merged into one, coverage values grouped per coverage,
  // and the coverages the referral doesn't have yet.
  const batch = () => {
    const values: Record<string, string> = {};
    const coverageValues = new Map<string, Record<string, string>>();
    queued.forEach((conflict) => {
      if (conflict.missingCoverage) return;
      if (conflict.facetId) {
        coverageValues.set(conflict.facetId, { ...coverageValues.get(conflict.facetId), ...conflict.values });
      } else {
        Object.assign(values, conflict.values);
      }
    });
    return { values, coverageValues, newCoverages: queued.filter((conflict) => conflict.missingCoverage) };
  };

  const save = async (restartActionIds?: string[]) => {
    const workflowId = workflow.id;
    const { values, coverageValues, newCoverages } = batch();
    // The run as the last coverage write left it — see stillCurrentRestartIds.
    let settled: Workflow | undefined;
    try {
      // Coverages first, awaited, and the referral write — which carries the consent — last: the order
      // Edit Demographics uses, so no restart it carries runs against the coverage as it was, and no new
      // coverage has its checks created against a referral already re-running. Each coverage write re-runs
      // that coverage's finished checks itself, which is why the consent is filtered below.
      for (const conflict of newCoverages) {
        // Filed at the packet's rank, under its label — the label is the claim on that rank that stops
        // a later extraction minting a second coverage there (see createFacet).
        const created = await addCoverage({
          workflowId,
          dimension: 'coverage',
          position: conflict.missingCoverage!.position,
          label: conflict.missingCoverage!.label,
          values: conflict.values,
        });
        if (created instanceof Workflow) settled = created;
      }
      for (const [facetId, facetValues] of coverageValues) {
        const written = await writeCoverage({ workflowId, facetId, values: facetValues });
        if (written instanceof Workflow) settled = written;
      }
      const restartIds = settled ? stillCurrentRestartIds(settled, restartActionIds) : restartActionIds;
      if (Object.keys(values).length > 0 || restartIds?.length) {
        await writeReferral({
          workflowId,
          ...(Object.keys(values).length > 0 ? { values } : {}),
          restartActionIds: restartIds,
        });
      }
    } catch {
      // The mutation already raised its toast. Keep the queue (and the consent step) so it can be retried.
      return;
    }
    setPendingRestart(null);
    setQueuedKeys([]);
  };

  const apply = () => {
    if (queued.length === 0) return;
    const { values, coverageValues } = batch();
    const actions = impactedActionsFor(workflow, {
      names: Object.keys(values),
      coverages: [...coverageValues].map(([facetId, facetValues]) => ({ facetId, names: Object.keys(facetValues) })),
    });
    if (actions.length > 0) {
      setPendingRestart(actions);
      return;
    }
    save();
  };

  const dismiss = (conflict: DemographicConflict) =>
    writeDismissals({
      workflowId: workflow.id,
      actionId: action.id,
      outputs: { [DISMISSALS_OUTPUT]: dismissalsFor(conflict) },
    })
      .then(() => setQueuedKeys((keys) => keys.filter((key) => key !== conflict.key)))
      .catch(() => undefined);

  return {
    queue: (conflict: DemographicConflict) =>
      setQueuedKeys((keys) => (keys.includes(conflict.key) ? keys : [...keys, conflict.key])),
    unqueue: (conflict: DemographicConflict) => setQueuedKeys((keys) => keys.filter((key) => key !== conflict.key)),
    isQueued: (conflict: DemographicConflict) => queuedKeys.includes(conflict.key),
    queuedCount: queued.length,
    discard: () => setQueuedKeys([]),
    apply,
    dismiss,
    pendingRestart,
    confirmRestart: () => pendingRestart && save(pendingRestart.map((impacted) => impacted.id)),
    cancelRestart: () => setPendingRestart(null),
    // Apply's own writes, apart from a Dismiss: only these spin the Apply button and the consent step.
    applying: writingReferral || writingCoverage || addingCoverage,
    // Anything in flight — what keeps every answer disabled until it settles.
    saving: writingReferral || writingCoverage || addingCoverage || dismissing,
  };
}
